import React, { useState, useEffect } from 'react';
import Chart from "react-apexcharts";
import { Dashboard_data, MediaUrl, List_country, List_city } from '../../Utils/Api';
import EmptyState from '../../Common/EmptyState';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  // Filter states
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [filters, setFilters] = useState({
    location_id: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchCountries = async () => {
    try {
      const response = await List_country();
      if (response && response.data?.data) {
        setCountries(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchCities = async (countryId) => {
    try {
      const response = await List_city(countryId);
      if (response && response.data?.data) {
        setCities(response.data.data);
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await Dashboard_data(params);
      if (response.data && response.data.data) {
        const rawData = response.data.data;
        
        // Process financial values (cents to dollars)
        if (rawData.summary_metrics) {
          rawData.summary_metrics.total_platform_revenue = (rawData.summary_metrics.total_platform_revenue || 0) / 100;
          rawData.summary_metrics.total_transaction_value = (rawData.summary_metrics.total_transaction_value || 0) / 100;
        }

        if (rawData.graph_metrics?.revenue_over_time) {
          rawData.graph_metrics.revenue_over_time = rawData.graph_metrics.revenue_over_time.map(item => ({
            ...item,
            revenue: (item.revenue || 0) / 100
          }));
        }

        if (rawData.regional_metrics?.revenue_by_region) {
          rawData.regional_metrics.revenue_by_region = rawData.regional_metrics.revenue_by_region.map(item => ({
            ...item,
            revenue: (item.revenue || 0) / 100
          }));
        }

        setData(rawData);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    setSelectedCountry(countryId);
    setFilters(prev => ({ ...prev, location_id: '' }));
    if (countryId) {
      fetchCities(countryId);
    } else {
      setCities([]);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      location_id: '',
      start_date: '',
      end_date: ''
    });
    setSelectedCountry('');
    setCities([]);
  };

  if (loading && !data) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-4 text-center mt-5"><h5>No Data Available</h5><button className="btn btn-primary mt-3" onClick={fetchData}>Retry</button></div>;

  const { summary_metrics, leaderboards, graph_metrics, regional_metrics, business_metrics } = data || {};

  // Chart Configurations
  const userGrowthSeries = [
    {
      name: 'Skillsters',
      data: (graph_metrics?.skillsters_growth || []).map(item => item.count || 0)
    },
    {
      name: 'Gigsters',
      data: (graph_metrics?.gigsters_growth || []).map(item => item.count || 0)
    }
  ];

  const userGrowthOptions = {
    chart: { type: 'area', height: 350, toolbar: { show: false } },
    colors: ['#00E396', '#008FFB'],
    stroke: { curve: 'smooth', width: 2 },
    xaxis: {
      categories: (graph_metrics?.skillsters_growth || []).map(item => {
        const date = new Date(item._id);
        return !isNaN(date.getTime()) ? date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : item._id;
      })
    },
    dataLabels: { enabled: false },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 } }
  };

  const revenueSeries = [{
    name: 'Revenue',
    data: (graph_metrics?.revenue_over_time || []).map(item => item.revenue || 0)
  }];

  const revenueOptions = {
    chart: { type: 'bar', height: 350, toolbar: { show: false } },
    colors: ['#775DD0'],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
    xaxis: {
      categories: (graph_metrics?.revenue_over_time || []).map(item => {
        const date = new Date(item._id);
        return !isNaN(date.getTime()) ? date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : item._id;
      })
    },
    yaxis: { labels: { formatter: (val) => `$${val.toFixed(0)}` } }
  };

  return (
    <div className="main-content">
      <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
        <h1 className="page-title fw-semibold fs-18 mb-0">Admin Dashboard</h1>
        <div className="ms-md-1 ms-0">
          <nav>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><a href="#">Home</a></li>
              <li className="breadcrumb-item active" aria-current="page">Dashboard</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Filters */}
      <div className="card custom-card">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Country</label>
              <select className="form-select" value={selectedCountry} onChange={handleCountryChange}>
                <option value="">Select Country</option>
                {countries.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">City</label>
              <select className="form-select" name="location_id" value={filters.location_id} onChange={handleFilterChange} disabled={!selectedCountry}>
                <option value="">All Cities</option>
                {cities.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Start Date</label>
              <input type="date" className="form-control" name="start_date" value={filters.start_date} onChange={handleFilterChange} />
            </div>
            <div className="col-md-2">
              <label className="form-label">End Date</label>
              <input type="date" className="form-control" name="end_date" value={filters.end_date} onChange={handleFilterChange} />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button className="btn btn-outline-danger w-100" onClick={clearFilters}>
                <i className="bx bx-reset me-1"></i> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="row">
        {[
          { title: 'Total Users', value: summary_metrics?.total_users || 0, icon: 'bx bx-user', color: 'primary' },
          { title: 'Skillsters', value: summary_metrics?.total_skillsters || 0, icon: 'bx bx-briefcase', color: 'success' },
          { title: 'Gigsters', value: summary_metrics?.total_gigsters || 0, icon: 'bx bx-buildings', color: 'info' },
          { title: 'Total Gigs', value: summary_metrics?.total_gigs || 0, icon: 'bx bx-layer', color: 'warning' },
          { title: 'Active Gigs', value: summary_metrics?.active_gigs || 0, icon: 'bx bx-task', color: 'secondary' },
          { title: 'Platform Revenue', value: `$${(summary_metrics?.total_platform_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, icon: 'bx bx-dollar', color: 'danger' },
          { title: 'Total Txn Value', value: `$${(summary_metrics?.total_transaction_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, icon: 'bx bx-trending-up', color: 'primary' }
        ].map((metric, index) => (
          <div className={index < 5 ? "col-xxl-2 col-xl-4 col-md-6" : "col-xxl-3 col-xl-6 col-md-6"} key={index}>
            <div className="card custom-card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <span className={`avatar avatar-md p-2 bg-${metric.color}-transparent text-${metric.color}`}>
                      <i className={`${metric.icon} fs-5`}></i>
                    </span>
                  </div>
                  <div className="flex-fill">
                    <h5 className="fw-semibold mb-1">{metric.value}</h5>
                    <p className="mb-0 fs-12 text-muted">{metric.title}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Business Efficiency Metrics */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">Business Efficiency</div>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-4 border-end">
                  <p className="text-muted mb-1">Acceptance Rate</p>
                  <h4 className="fw-bold text-success">{business_metrics?.acceptance_rate?.toFixed(1) || 0}%</h4>
                  <div className="progress progress-sm mt-2 mx-auto" style={{ width: '80%' }}>
                    <div className="progress-bar bg-success" role="progressbar" style={{ width: `${business_metrics?.acceptance_rate || 0}%` }}></div>
                  </div>
                </div>
                <div className="col-md-4 border-end">
                  <p className="text-muted mb-1">Completion Rate</p>
                  <h4 className="fw-bold text-primary">{business_metrics?.completion_rate?.toFixed(1) || 0}%</h4>
                  <div className="progress progress-sm mt-2 mx-auto" style={{ width: '80%' }}>
                    <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${business_metrics?.completion_rate || 0}%` }}></div>
                  </div>
                </div>
                <div className="col-md-4">
                  <p className="text-muted mb-1">Avg Acceptance Time</p>
                  <h4 className="fw-bold text-warning">
                    {business_metrics?.avg_gig_acceptance_time_seconds 
                      ? (business_metrics.avg_gig_acceptance_time_seconds / 3600).toFixed(1) 
                      : 0} hrs
                  </h4>
                  <p className="fs-11 text-muted">Marketplace speed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Charts Row */}
      <div className="row">
        <div className="col-xl-6">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">User Growth Trends</div>
            </div>
            <div className="card-body">
              <Chart options={userGrowthOptions} series={userGrowthSeries} type="area" height={350} />
            </div>
          </div>
        </div>
        <div className="col-xl-6">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">Revenue Performance</div>
            </div>
            <div className="card-body">
              <Chart options={revenueOptions} series={revenueSeries} type="bar" height={350} />
            </div>
          </div>
        </div>
      </div>

      {/* Regional & Quality Row */}
      <div className="row">
        {/* Regional Insights */}
        <div className="col-xl-8">
          <div className="card custom-card">
            <div className="card-header d-flex justify-content-between">
              <div className="card-title">Regional Insights</div>
              <ul className="nav nav-tabs card-header-tabs border-bottom-0" role="tablist">
                <li className="nav-item">
                  <a className="nav-link active" data-bs-toggle="tab" href="#users-region" role="tab">Users</a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" data-bs-toggle="tab" href="#revenue-region" role="tab">Revenue</a>
                </li>
              </ul>
            </div>
            <div className="card-body">
              <div className="tab-content">
                <div className="tab-pane show active" id="users-region" role="tabpanel">
                  <div className="table-responsive">
                    <table className="table text-nowrap table-hover">
                      <thead>
                        <tr>
                          <th>Region</th>
                          <th className="text-center">Total Users</th>
                          <th className="text-center">Skillsters</th>
                          <th className="text-center">Gigsters</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(regional_metrics?.users_by_region || []).sort((a,b) => b.count - a.count).map((region, i) => (
                          <tr key={i}>
                            <td>{region.name || 'Unknown'}</td>
                            <td className="text-center fw-semibold">{region.count}</td>
                            <td className="text-center text-success">{region.skillsters}</td>
                            <td className="text-center text-info">{region.gigsters}</td>
                          </tr>
                        ))}
                        {(!regional_metrics?.users_by_region?.length) && <tr><td colSpan="4" className="text-center">No regional data available</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="tab-pane" id="revenue-region" role="tabpanel">
                  <div className="table-responsive">
                    <table className="table text-nowrap table-hover">
                      <thead>
                        <tr>
                          <th>Region</th>
                          <th className="text-end">Revenue</th>
                          <th className="text-center">Transaction Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(regional_metrics?.revenue_by_region || []).sort((a,b) => b.revenue - a.revenue).map((region, i) => (
                          <tr key={i}>
                            <td>{region.name || 'Unknown'}</td>
                            <td className="text-end fw-semibold text-success">${region.revenue?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td className="text-center">{region.count}</td>
                          </tr>
                        ))}
                        {(!regional_metrics?.revenue_by_region?.length) && <tr><td colSpan="3" className="text-center">No revenue data available</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quality Insights / Top Performers */}
        <div className="col-xl-4">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">Top Rated Professionals</div>
            </div>
            <div className="card-body p-0">
              <ul className="list-group list-group-flush">
                {(leaderboards?.top_rated_skillsters && leaderboards.top_rated_skillsters.length > 0) ? (
                  leaderboards.top_rated_skillsters.map((skillster) => (
                    <li key={skillster._id} className="list-group-item d-flex align-items-center py-3">
                      <span className="avatar avatar-md me-3">
                        <img src={skillster.profilePicture ? `${MediaUrl}${skillster.profilePicture}` : "assets/images/faces/9.jpg"} className="rounded-circle" alt="img" />
                      </span>
                      <div className="flex-fill">
                        <div className="fw-semibold">{skillster.name}</div>
                        <div className="text-warning fs-12">
                          <i className="bx bxs-star"></i> {skillster.rating?.toFixed(1)} <span className="text-muted">({skillster.count} gigs)</span>
                        </div>
                      </div>
                    </li>
                  ))
                ) : (
                  <div className="p-4"><EmptyState message="No rated skillsters found" /></div>
                )}
              </ul>
            </div>
            <div className="card-footer text-center">
              <a href="/users" className="text-primary fs-12 fw-semibold">View All Professionals <i className="bx bx-right-arrow-alt align-middle"></i></a>
            </div>
          </div>
        </div>
      </div>

      {/* Top Gigs Row */}
      <div className="row">
        <div className="col-xl-12">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">Highest Value Gigs</div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table text-nowrap table-hover border-top">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-4">Gig Title</th>
                      <th>Date</th>
                      <th className="text-end pe-4">Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(leaderboards?.top_paid_gigs && leaderboards.top_paid_gigs.length > 0) ? (
                      leaderboards.top_paid_gigs.map((gig) => (
                        <tr key={gig._id}>
                          <td className="ps-4 fw-semibold">{gig.title}</td>
                          <td className="text-muted">{new Date(gig.date).toLocaleDateString()}</td>
                          <td className="text-end pe-4"><span className="badge bg-success-transparent fs-13">${gig.payout?.toLocaleString()}</span></td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="3" className="text-center py-4"><EmptyState message="No paid gigs to show" /></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
